/**
 * The training catalog as the pages show it (spec 53 sections 8.1 and 10).
 *
 * Pure functions over GET /api/training/modules' `modules`:
 *   [{ id, title, summary, minutes, version, course, chapter, group,
 *      required, status, passed_version, passed_at, assisted }]
 *
 * A module's video is not in this list: the module page asks
 * GET /api/training/modules/:id/video for it (TrainingModule.vue).
 *
 * `status` is where the person stands (the server's required.statusOf,
 * training plan 5.3): `passed` at the current version, `outdated` (passed an
 * older version that still counts), `voided` (passed before a retrain
 * version, so it's a retake), or `not_started`.
 *
 * Two courses, taken in order, and the groups, taken in any order. The course
 * titles are courses.js's (the card's course bar says the same words).
 */
import { COURSES } from './courses.js';

export const GROUPS = Object.freeze({
  // Decision D5's modules (time-off, rate-con-load; settle-pay), listed
  // after the courses they grow out of.
  dispatching: 'More dispatching',
  pay: 'Paying drivers',
  company: 'Your company',
  documents: 'Documents',
  fuel: 'Fuel and miles',
  phone: 'Optional, needs a phone',
});

const SECTION_ORDER = [
  'course:dispatch',
  'course:billing',
  ...Object.keys(GROUPS).map(g => `group:${g}`),
];

export const STATUS = Object.freeze({
  PASSED: 'passed',
  OUTDATED: 'outdated',
  VOIDED: 'voided',
  NOT_STARTED: 'not_started',
});

/** Does the module count as passed? An older version that still counts does. */
export function isPassed(module) {
  return Boolean(module && (module.status === STATUS.PASSED || module.status === STATUS.OUTDATED));
}

/**
 * The status's words for a list (training plan 5.5's badges), or null for
 * not started, where a list says how long the module takes instead.
 */
export function statusLabel(module) {
  switch (module && module.status) {
    case STATUS.PASSED:
      return 'Passed';
    case STATUS.OUTDATED:
      return 'Passed (older version)';
    case STATUS.VOIDED:
      return 'Out of date: retake';
    default:
      return null;
  }
}

function sectionKey(module) {
  if (module.course) return `course:${module.course}`;
  return `group:${module.group || 'company'}`;
}

function sectionTitle(key) {
  const [kind, name] = key.split(':');
  if (kind === 'course') return (COURSES[name] && COURSES[name].title) || name;
  return GROUPS[name] || name;
}

/**
 * Whether a course chapter is still shut: it opens once the chapter before it
 * passed (8.1). A required chapter is never shut, because a role's required
 * set skips chapters (10.2: an admin starts at Assign, and its stage is built
 * for them), and a chapter outside a course never is.
 */
export function isLocked(module, modules) {
  if (!module || !module.course || !Number.isInteger(module.chapter) || module.chapter <= 1) {
    return false;
  }
  if (module.required) return false;
  const previous = (modules || []).find(
    m => m.course === module.course && m.chapter === module.chapter - 1
  );
  return Boolean(previous) && !isPassed(previous);
}

/**
 * The catalog in sections: [{ key, kind, title, modules, required }], course
 * chapters in order. With `requiredFirst`, the sections holding something
 * required for this person come first (8.1, the practice-side list), keeping
 * the house order otherwise.
 */
export function sectionsOf(modules, { requiredFirst = false } = {}) {
  const byKey = new Map();
  for (const module of modules || []) {
    const key = sectionKey(module);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(module);
  }
  const rank = key => {
    const at = SECTION_ORDER.indexOf(key);
    return at === -1 ? SECTION_ORDER.length : at;
  };
  const sections = [...byKey.entries()]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([key, list]) => ({
      key,
      kind: key.startsWith('course:') ? 'course' : 'group',
      title: sectionTitle(key),
      modules: key.startsWith('course:')
        ? list.slice().sort((a, b) => (a.chapter || 0) - (b.chapter || 0))
        : list,
      required: list.some(m => m.required),
    }));
  if (!requiredFirst) return sections;
  return [...sections.filter(s => s.required), ...sections.filter(s => !s.required)];
}

/** This person's required modules, in course order then the groups'. */
export function requiredOf(modules) {
  return sectionsOf(modules).flatMap(s => s.modules.filter(m => m.required));
}

/**
 * About how long what's left takes, in minutes, rounded up to five ("About
 * 35 minutes"). Zero when nothing is left.
 */
export function minutesLeft(modules) {
  const total = (modules || [])
    .filter(m => !isPassed(m))
    .reduce((sum, m) => sum + (Number(m.minutes) || 0), 0);
  return total > 0 ? Math.ceil(total / 5) * 5 : 0;
}

/** The chapter after this one in its course, or null. */
export function nextChapter(module, modules) {
  if (!module || !module.course || !Number.isInteger(module.chapter)) return null;
  return (
    (modules || []).find(m => m.course === module.course && m.chapter === module.chapter + 1) ||
    null
  );
}
